// Context-sensitive help content. Keyed by "<scope>.<page>" (e.g. muni.forecast).
// Each entry has DE + EN {title, body[]}. Keep language simple and action-oriented.

const HELP = {
  // ---------- Public / auth ----------
  landing: {
    de: {
      title: "Willkommen beim Water Planner",
      body: [
        "Water Planner koordiniert Poolbefüllungen in Zeiten knappen Wassers.",
        "Wählen Sie oben Ihre Rolle: Gemeinde, Wasserversorger oder Poolbesitzer:in.",
        "Klicken Sie auf „Jetzt starten“, um ein Konto anzulegen — oder nutzen Sie das Waitlist-Formular, wenn Sie den Piloten für Ihre Gemeinde anfragen möchten.",
      ],
    },
    en: {
      title: "Welcome to Water Planner",
      body: [
        "Water Planner coordinates pool fillings when water is scarce.",
        "Pick your role above: municipality, utility or pool owner.",
        "Tap \"Get started\" to create an account — or use the waitlist form to request the pilot for your municipality.",
      ],
    },
  },
  login: {
    de: {
      title: "Anmelden",
      body: [
        "Melden Sie sich mit E-Mail + Passwort an, oder nutzen Sie „Sign in with Google“.",
        "Passwort vergessen? Über den Link unter dem Anmelde-Button erhalten Sie einen Reset-Link per E-Mail (1 Stunde gültig).",
      ],
    },
    en: {
      title: "Sign in",
      body: [
        "Sign in with email + password, or use \"Sign in with Google\".",
        "Forgot your password? Use the link below the sign-in button — you'll receive a reset email valid for one hour.",
      ],
    },
  },
  register: {
    de: {
      title: "Konto erstellen",
      body: [
        "Wählen Sie Ihre Rolle. Gemeinde-Konten legen automatisch eine neue Gemeinde an.",
        "Poolbesitzer:innen wählen ihre Gemeinde aus der Liste.",
        "Wasserversorger können mehrere Gemeinden gleichzeitig betreuen.",
      ],
    },
    en: {
      title: "Create account",
      body: [
        "Pick your role. Municipality accounts create a new municipality automatically.",
        "Pool owners pick their municipality from the list.",
        "Utilities can supervise several municipalities at once.",
      ],
    },
  },
  forgot_password: {
    de: {
      title: "Passwort zurücksetzen",
      body: [
        "Geben Sie Ihre E-Mail-Adresse ein. Wenn ein Konto existiert, erhalten Sie einen Link.",
        "Aus Sicherheitsgründen sehen alle E-Mail-Adressen dieselbe Bestätigungsmeldung.",
        "Der Link ist 1 Stunde gültig und kann einmal verwendet werden.",
      ],
    },
    en: {
      title: "Reset password",
      body: [
        "Enter your email address. If an account exists, you'll receive a reset link.",
        "For safety, every address sees the same confirmation message.",
        "The link is valid for one hour and can be used once.",
      ],
    },
  },
  reset_password: {
    de: {
      title: "Neues Passwort setzen",
      body: [
        "Wählen Sie ein neues Passwort. Die Stärke wird direkt beim Tippen angezeigt.",
        "Alle bestehenden Sitzungen werden nach dem Speichern abgemeldet — melden Sie sich mit dem neuen Passwort neu an.",
      ],
    },
    en: {
      title: "Set a new password",
      body: [
        "Choose a new password. Strength is shown live as you type.",
        "All existing sessions are signed out after saving — sign in again with the new password.",
      ],
    },
  },
  onboarding: {
    de: {
      title: "Konto einrichten",
      body: [
        "Wählen Sie Ihre Rolle und, falls Poolbesitzer:in, Ihre Gemeinde.",
        "Diese Angaben können Sie später jederzeit ändern.",
      ],
    },
    en: {
      title: "Set up your account",
      body: [
        "Pick your role and, if a pool owner, your municipality.",
        "You can change these details anytime later.",
      ],
    },
  },

  // ---------- Municipality ----------
  // ---------- Municipality ----------
  "muni.overview": {
    de: {
      title: "Übersicht — Gemeinde",
      body: [
        "Die vier Kacheln zeigen heute verfügbar, heute genutzt, Anzahl aktiver Bürger:innen und Buchungen insgesamt.",
        "Der Balken „Kontingent-Nutzung“ fasst die kommenden 14 Tage zusammen — rot heißt: Grenze fast erreicht.",
      ],
    },
    en: {
      title: "Overview — Municipality",
      body: [
        "The four tiles show what's available today, used today, active citizens and total bookings.",
        "The \"Quota usage\" bar summarises the next 14 days — red means the limit is nearly reached.",
      ],
    },
  },
  "muni.bookings": {
    de: {
      title: "Alle Buchungen",
      body: [
        "Filtern Sie nach Status. Mit den Checkboxen wählen Sie mehrere Buchungen für die Sammel-Freigabe aus.",
        "„Freigeben“ öffnet einen Dialog mit vorausgefüllter Nachricht — Platzhalter wie {name} oder {volume} werden automatisch pro Buchung ersetzt.",
        "Nach einer Sammel-Freigabe haben Sie 10 Sekunden Zeit, per „Rückgängig“ abzubrechen, bevor E-Mails rausgehen.",
      ],
    },
    en: {
      title: "All bookings",
      body: [
        "Filter by status. Use the checkboxes to pick several bookings for bulk approval.",
        "\"Approve\" opens a dialog with a prefilled message — placeholders like {name} or {volume} are substituted per booking.",
        "After a bulk approval you have 10 seconds to hit \"Undo\" before emails go out.",
      ],
    },
  },
  "muni.forecast": {
    de: {
      title: "Prognose",
      body: [
        "Die Wochenprognose bündelt die kommenden 30 Tage in 7-Tage-Blöcke. Farben zeigen die Auslastung — rot ab 90 %.",
        "„Als CSV herunterladen“ gibt die Wochenwerte im UTF-8-Format aus, ideal für die Weitergabe an den Wasserversorger.",
        "Das Liniendiagramm überlagert Buchungen, historischen Wochentagsdurchschnitt und Kontingent.",
      ],
    },
    en: {
      title: "Forecast",
      body: [
        "The weekly rollup groups the next 30 days into 7-day blocks. Colours show usage — red at ≥90 %.",
        "\"Download CSV\" exports the weekly values in UTF-8, ideal for sharing with the utility.",
        "The line chart overlays bookings, weekday historical average and daily quota.",
      ],
    },
  },
  "muni.calendar": {
    de: {
      title: "Kalender",
      body: [
        "Grün = viel Kontingent frei. Gelb = knapp. Rot = ausgebucht.",
        "Ein Klick auf einen Tag öffnet die Liste der Buchungen für dieses Datum.",
      ],
    },
    en: {
      title: "Calendar",
      body: [
        "Green = plenty of quota free. Yellow = tight. Red = fully booked.",
        "Click a day to open its bookings.",
      ],
    },
  },
  "muni.messages": {
    de: {
      title: "Nachrichten",
      body: [
        "Direkte Kommunikation mit Poolbesitzer:innen Ihrer Gemeinde. Antworten sind für die Bürger:innen sichtbar.",
        "Wenn eine „Reply-To“-Adresse in den Einstellungen gesetzt ist, können Bürger:innen direkt auf Ihre System-E-Mails antworten.",
      ],
    },
    en: {
      title: "Messages",
      body: [
        "Direct line to pool owners in your municipality. Replies are visible to citizens.",
        "If a Reply-To address is set in Settings, citizens can reply directly to your system emails.",
      ],
    },
  },
  "muni.large": {
    de: {
      title: "Großverbraucher",
      body: [
        "Anfragen von Landwirtschaft, Industrie oder Veranstaltungen.",
        "Freigegebene Großverbraucher-Buchungen werden vom Tageskontingent abgezogen.",
      ],
    },
    en: {
      title: "Large consumers",
      body: [
        "Requests from agriculture, industry or events.",
        "Approved large-consumer bookings are deducted from the daily quota.",
      ],
    },
  },
  "muni.settings": {
    de: {
      title: "Einstellungen",
      body: [
        "Kontingent (m³/Tag), Meldepflicht Nachfüllen und Reply-To-Adresse werden hier gepflegt.",
        "Freigabe-Vorlagen in DE und EN unterstützen Platzhalter: {name}, {date}, {volume}, {municipality}.",
        "Signaturen können separat pro Sprache eingestellt und per Toggle (Freigabe / Ablehnung / Abschluss) an- oder abgeschaltet werden. Die Vorschau lässt sich zwischen DE und EN umschalten.",
      ],
    },
    en: {
      title: "Settings",
      body: [
        "Daily quota (m³), refill reporting and Reply-To address live here.",
        "Approval templates in DE and EN support placeholders: {name}, {date}, {volume}, {municipality}.",
        "Signatures can be set per language and toggled per email type (approval / rejection / completion). The preview switches between DE and EN.",
      ],
    },
  },

  // ---------- Utility ----------
  "util.overview": {
    de: {
      title: "Übersicht — Wasserversorger",
      body: [
        "Regionale Kennzahlen über alle betreuten Gemeinden. Der Ausschnitt lässt sich rechts oben anpassen.",
      ],
    },
    en: {
      title: "Overview — Utility",
      body: [
        "Regional metrics across every municipality you cover. Adjust the scope from the top right.",
      ],
    },
  },
  "util.forecast": {
    de: {
      title: "Prognose — 30 Tage",
      body: [
        "Regionale Bedarfskurve inklusive Prognose auf Basis historischer Wochentage.",
        "Passen Sie den Ausschnitt an, um einzelne Gemeinden getrennt zu betrachten.",
      ],
    },
    en: {
      title: "Forecast — 30 days",
      body: [
        "Regional demand curve including a forecast based on historical weekdays.",
        "Adjust the scope to isolate individual municipalities.",
      ],
    },
  },
  "util.munis": {
    de: {
      title: "Alle Gemeinden",
      body: [
        "Übersicht Ihrer betreuten Gemeinden inklusive Kontingent und Nutzung.",
        "Über die „Gemeinden auswählen“-Auswahl legen Sie fest, welche Sie sehen möchten.",
      ],
    },
    en: {
      title: "All municipalities",
      body: [
        "Overview of every municipality you serve, including quota and usage.",
        "Use \"Select municipalities\" to choose which ones you see.",
      ],
    },
  },
  "util.bookings": {
    de: {
      title: "Alle Buchungen (regional)",
      body: [
        "Read-only Sicht auf alle Buchungen im gewählten Ausschnitt.",
      ],
    },
    en: {
      title: "All bookings (regional)",
      body: [
        "Read-only view of every booking in the selected scope.",
      ],
    },
  },
  "util.large": {
    de: {
      title: "Großverbraucher — regional",
      body: [
        "Landwirtschaft, Industrie und Veranstaltungen mit Wasserbedarf. Freigaben erfolgen durch die Gemeinden.",
      ],
    },
    en: {
      title: "Large consumers — regional",
      body: [
        "Agriculture, industry and events with water demand. Approvals stay with the municipalities.",
      ],
    },
  },
  "util.calendar": {
    de: {
      title: "Kalender",
      body: [
        "Wochenverlauf der Nutzung über den gewählten Ausschnitt.",
      ],
    },
    en: {
      title: "Calendar",
      body: [
        "Weekly usage across the selected scope.",
      ],
    },
  },

  // ---------- Pool owner ----------
  "owner.overview": {
    de: {
      title: "Übersicht — Poolbesitzer:in",
      body: [
        "Sie sehen aktuelle Verfügbarkeit Ihrer Gemeinde, Ihre nächste Buchung und offene Erinnerungen.",
        "Über „Neue Buchung“ reservieren Sie einen Tag im Kontingent — das Volumen wird automatisch aus Fläche × Tiefe berechnet.",
      ],
    },
    en: {
      title: "Overview — Pool owner",
      body: [
        "You see your municipality's live availability, your next booking and open reminders.",
        "Use \"New booking\" to reserve a day inside the quota — volume is calculated from area × depth.",
      ],
    },
  },
  "owner.book": {
    de: {
      title: "Neue Buchung",
      body: [
        "Wählen Sie ein Datum aus dem Kalender — grüne Tage haben genug Restkontingent.",
        "Fläche × Tiefe ergibt das Volumen; die Gemeinde erhält die Anfrage automatisch per E-Mail.",
      ],
    },
    en: {
      title: "New booking",
      body: [
        "Pick a date from the calendar — green days still have quota available.",
        "Area × depth gives the volume; the municipality is notified automatically by email.",
      ],
    },
  },
  "owner.calendar": {
    de: {
      title: "Kalender",
      body: [
        "Grün = viel Kontingent frei. Gelb = knapp. Rot = ausgebucht.",
      ],
    },
    en: {
      title: "Calendar",
      body: [
        "Green = plenty of quota free. Yellow = tight. Red = fully booked.",
      ],
    },
  },
  "owner.measurements": {
    de: {
      title: "Messwerte",
      body: [
        "Erfassen Sie pH, freies Chlor und Salzgehalt regelmäßig.",
        "Der Verlauf hilft, Chemie-Ausgaben und Nachfüll-Mengen im Blick zu behalten.",
      ],
    },
    en: {
      title: "Measurements",
      body: [
        "Log pH, free chlorine and salt regularly.",
        "The trend keeps chemistry costs and refill amounts under control.",
      ],
    },
  },
  "owner.reminders": {
    de: {
      title: "Erinnerungen",
      body: [
        "Wählen Sie Typ, Titel, Fälligkeit und Intervall. Fällige Erinnerungen erscheinen auf dem Dashboard und werden per E-Mail bestätigt.",
      ],
    },
    en: {
      title: "Reminders",
      body: [
        "Pick type, title, due date and interval. Due reminders appear on your dashboard and are confirmed by email.",
      ],
    },
  },
  "owner.messages": {
    de: {
      title: "Nachrichten",
      body: [
        "Direkter Draht zur Gemeindeverwaltung.",
      ],
    },
    en: {
      title: "Messages",
      body: [
        "Direct line to the town hall.",
      ],
    },
  },
  "owner.location": {
    de: {
      title: "Standort",
      body: [
        "Setzen Sie Ihre Adresse per Karte oder manuell. Der Standort wird der Gemeinde bei Anfragen mitgeschickt.",
      ],
    },
    en: {
      title: "Location",
      body: [
        "Set your address on the map or manually. Your location is sent to the municipality with bookings.",
      ],
    },
  },
  "owner.refill": {
    de: {
      title: "Nachfüllen melden",
      body: [
        "Bei aktivierter Meldepflicht Ihrer Gemeinde: Datum + Menge angeben. Nachfüllungen zählen zum Tageskontingent.",
      ],
    },
    en: {
      title: "Report refill",
      body: [
        "If your municipality requires refill reporting: enter date + volume. Refills count toward the daily quota.",
      ],
    },
  },
  "owner.large": {
    de: {
      title: "Großverbraucher-Anfrage",
      body: [
        "Sonderanfragen für hohe Volumina (z. B. Feste, Landwirtschaft). Die Gemeinde entscheidet manuell.",
      ],
    },
    en: {
      title: "Large-consumer request",
      body: [
        "Special requests for high volumes (events, agriculture). The municipality decides manually.",
      ],
    },
  },
};

const FALLBACK = {
  de: {
    title: "Hilfe",
    body: [
      "Über den Fragezeichen-Button oben rechts erhalten Sie kontext-sensitive Erklärungen — der Text passt sich Ihrer Rolle und dem aktuellen Bereich an.",
    ],
  },
  en: {
    title: "Help",
    body: [
      "The question-mark button top right shows context-sensitive help — the text adapts to your role and current section.",
    ],
  },
};

export function getHelpEntry(key, lang) {
  const entry = (key && HELP[key]) || FALLBACK;
  return entry[lang] || entry.de || FALLBACK.de;
}
